import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import {
  PrepaymentButton,
  PrepaymentInfo,
  PrepaymentPassengers,
} from 'pages/umrah/prepayment-page/components'

import { getUmrahPrepaymentPrice } from 'entities/umrah/libs/getUmrahPrepaymentPrice.ts'
import { getUmrahTourPrice } from 'entities/umrah/libs/getUmrahTourPrice.ts'
import umraListStore from 'entities/umrah/models/umrah-list.ts'
import userStore from 'entities/user/module/user-store'

import { BuyTicketApi } from 'shared/api/BuyTicket.ts'
import { UmrahBuyTourApi } from 'shared/api/umrah/buy-tour'
import { ShareKeys } from 'shared/constants'
import ErrorImage from 'shared/images/error.png'
import { getErrorMessage } from 'shared/lib/errorText.ts'
import { getRawColor } from 'shared/lib/getRawColor.ts'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import s from './styles.module.scss'

const UmrahPrepaymentPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { openDialog, closeDialog } = useDialogBottomSheet()

  const [loading, setLoading] = useState(false)
  const [passengers, setPassengers] = useState(1)

  const tour = umraListStore.getUmrahTour()

  const tourPrice = useMemo(
    () => getUmrahTourPrice(tour?.price, passengers),
    [tour?.price, passengers],
  )
  const prepaymentPrice = useMemo(
    () => getUmrahPrepaymentPrice(tourPrice),
    [tourPrice],
  )

  useEffect(() => {
    const raw = userStore.getClientStateParse<{
      tour?: ReturnType<typeof umraListStore.getUmrahTour>
    }>(ShareKeys.umrah)
    if (!raw || typeof raw !== 'object' || !raw.tour) return

    umraListStore.setUmrahTour(raw.tour)
  }, [userStore.client_state])

  const onChangeCount = (value: number) => {
    setPassengers(value)
  }

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
        />
      ),
      isDialog: false,
    })
  }

  const onRegularPay = async () => {
    try {
      setLoading(true)

      const bookData = await firstTouchOrder()

      if (bookData) void onRequestBuyTicket(bookData?.id)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const firstTouchOrder = async () => {
    try {
      setLoading(true)

      const preparedData = {
        umra_tour_id: tour?.id,
        schedule_id: tour?.schedule?.id,
        pilgrim_count: passengers,
      }

      const response = await UmrahBuyTourApi.onConfirmPayment(preparedData)
      return response?.data?.data
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onRequestBuyTicket = async (id: string) => {
    try {
      setLoading(true)

      const { data } = await BuyTicketApi.buyTicketConfirm({ id, type: 'umra' })
      await startPaymentFromConfirmResult(data)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!tour) {
      navigate(-1)
    }
  }, [])

  return (
    <div className={s.container}>
      <HeaderGoBack
        title={t('umrah_block.tour_info')}
        arrowColor={getRawColor('--black')}
        buttonWhite
      />

      {loading ? (
        <Loader marginTop={130} />
      ) : (
        <>
          <PrepaymentInfo />

          <PrepaymentPassengers
            onChange={onChangeCount}
            passengers={passengers}
          />

          <PrepaymentButton
            price={tourPrice}
            prepayment={prepaymentPrice}
            passengers={passengers}
            onClick={onRegularPay}
          />
        </>
      )}
    </div>
  )
})

export default UmrahPrepaymentPage
